import { useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Tooltip, SkeletonLine } from '@crazyegginc/hatch';
import { atom, useAtom } from 'jotai';

import { DisplayCurrency } from '/src/features/_global/components/DisplayCurrency';
import { useSelection, useQueryParams } from '/src/hooks';

import { goalDetailSelectedTriggerType } from '/src/features/goals/store';

import { DetailCardHeaderElements } from '/src/features/goals/components/DetailCardHeaderElements';
import { DetailCardPopover } from '/src/features/goals/components/DetailCardPopover';
import { DetailCardTriggers } from '/src/features/goals/components/DetailCardTriggers';
import { DistributionChart } from '/src/features/goals/components/DistributionChart';
import { TRIGGER_CHART_TYPES } from '/src/features/goals/constants';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';

export const goalDistributionDatasetAtom = atom('conversions');

export function GoalDetailCard({ goal, counts, isFetchingCounts = true }) {
  const { id } = useParams();
  const { set: paramSet } = useQueryParams();
  const [type] = useAtom(goalDetailSelectedTriggerType);
  const [dataset, setDataset] = useAtom(goalDistributionDatasetAtom);
  const { currentSelection } = useSelection();
  const totalConversionsCount = counts?.conversionsCount ?? 0;
  const uniqueConversionCount = counts?.uniqueConversionsCount ?? 0;
  const triggersCounts = counts?.triggers ?? [];

  const hasValueSet = goal?.triggers?.some?.((trigger) => !!trigger.valueType) ?? false;
  const hasValueSetAndAmount = hasValueSet && goal?.totalValue?.displayWorth >= 0 && goal?.hasValue;

  const isLoading = !goal;

  useEffect(() => {
    const param = new URLSearchParams(window.location.search);
    if (!param.get('view')) {
      paramSet('conversions');
    }
  }, [paramSet]);

  const setView = useCallback(
    (value) => {
      setDataset(value);
      paramSet('view', value);
    },
    [setDataset, paramSet],
  );

  return (
    <div className="w-full">
      <div
        className="flex flex-col items-center rounded-[5px] border border-mystic-500 bg-white"
        style={{
          boxShadow: '0 4px 6px 0 rgba(7,31,49,0.07)',
        }}
      >
        <DetailCardHeaderElements>
          <DetailCardHeaderElements.Item
            active={dataset === 'conversions'}
            onCheck={() => {
              setDataset('conversions');
              setView('conversions');
            }}
          >
            <DetailCardHeaderElements.Title>
              <div className="flex items-center">
                Total Conversions
                <Tooltip
                  placement="bottom"
                  tooltipContent={
                    <div className="w-[335px] px-2 py-2.5">
                      <h5 className="mb-2.5 text-xs font-bold text-white">
                        Does this look different to your A/B test conversions?
                      </h5>
                      <p className="text-xs font-light leading-tight tracking-wide text-white">
                        Conversions for A/B tests are only counted if a visitor has landed on your test pages before
                        performing a goal, whereas all conversions are counted in your goals dashboard.
                      </p>
                    </div>
                  }
                >
                  <InfoIcon
                    className="h-3 w-3 -translate-y-px transform fill-current text-dodger-blue-300 ml-2.5"
                    aria-label="Info on conversion"
                  />
                </Tooltip>
              </div>
            </DetailCardHeaderElements.Title>
            {isFetchingCounts ? (
              <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
            ) : (
              <DetailCardHeaderElements.Content>
                {totalConversionsCount.toLocaleString()}
              </DetailCardHeaderElements.Content>
            )}
          </DetailCardHeaderElements.Item>

          <DetailCardHeaderElements.Item
            active={dataset === 'visitors'}
            onCheck={() => {
              setDataset('visitors');
              setView('visitors');
            }}
          >
            <DetailCardHeaderElements.Title>Visitors Converting</DetailCardHeaderElements.Title>
            {isFetchingCounts ? (
              <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
            ) : (
              <DetailCardHeaderElements.Content>
                {uniqueConversionCount.toLocaleString()}
              </DetailCardHeaderElements.Content>
            )}
          </DetailCardHeaderElements.Item>

          <div className="relative flex">
            <DetailCardHeaderElements.Item
              active={dataset === 'value'}
              isButton={hasValueSetAndAmount}
              blockType={!hasValueSetAndAmount}
              onCheck={() => {
                if (hasValueSetAndAmount) {
                  setDataset('value');
                  paramSet('value');
                }
              }}
            >
              <DetailCardHeaderElements.Title>
                Goal Value {hasValueSetAndAmount && `(${goal.totalValue.displayCurrency})`}
              </DetailCardHeaderElements.Title>
              <DetailCardHeaderElements.Content>
                {isFetchingCounts ? (
                  <SkeletonLine className="!h-[37px] !rounded-sm" width="100%" />
                ) : hasValueSetAndAmount ? (
                  <DisplayCurrency
                    value={{
                      displayWorth: goal.totalValue.displayWorth,
                      displayCurrency: goal.totalValue.displayCurrency,
                      isConverted: false,
                    }}
                    precision={10000}
                    textOnly={true}
                    appendCurrency={false}
                    isTruncate={true}
                  />
                ) : (
                  <Link to={`/goals/${id}/edit-value`} className="text-link">
                    Set goal value
                  </Link>
                )}
              </DetailCardHeaderElements.Content>
            </DetailCardHeaderElements.Item>
            {hasValueSetAndAmount && (
              <div className="absolute right-3 top-4 text-lynch-500">
                <DetailCardPopover hasValueSetAndAmount={hasValueSetAndAmount} />
              </div>
            )}
          </div>
        </DetailCardHeaderElements>
        <div className="py-10 border-b-2 border-mystic-500 w-full px-5">
          <div className="relative flex h-[300px] min-w-[500px] flex-1">
            {!isFetchingCounts && (
              <DistributionChart
                goal={goal}
                triggerSelection={currentSelection}
                dataset={dataset}
                isSeparate={type === TRIGGER_CHART_TYPES.SEPARATE}
              />
            )}
          </div>
        </div>
        {!isLoading && (
          <DetailCardTriggers
            goal={goal}
            triggersCounts={triggersCounts}
            hasValueSetAndAmount={hasValueSetAndAmount}
            isFetchingCounts={isFetchingCounts}
          />
        )}
      </div>
    </div>
  );
}
